import React, {useCallback, useContext, useEffect, useState} from 'react'
import {Col, Form, Row} from "react-bootstrap";
import {useHistory} from "react-router-dom";
import {WireTemplatesContext} from "./context";
import {SecurityContext} from "context/security";
import * as URL from "service/UrlConstant";
import WireTemplateWizardLayout from "./WireTemplateWizardLayout";
import ShiftEnterPopupMessage from "components/ShiftEnterPopupMessage";
import {
    getClearingSystemIdWithPrefix,
    getWireTemplateStepUrl,
    shouldNavigateToWireTemplateStep,
    USABA_CLEARING_SYSTEM_CODE,
    USABA_CLEARING_SYSTEM_ID_PREFIX,
    WIRE_TEMPLATE_STEP_URL
} from "./AddNewWireTemplateStep1";

const intermediaryAgentFields = [
    [
        {label: 'Agent BIC', name: 'intermediaryAgentBIC', md: 3, maxLength: 50, conditionallyRequired: true},
        {label: 'Clearing System Code', name: 'intermediaryAgentClearingSystemCode', md: 2, type: 'select', options: [{label: 'USABA', value: 'USABA'}], conditionallyRequired: true},
        {label: 'Clearing System ID', name: 'intermediaryAgentClearingSystemID', md: 4, maxLength: 50, conditionallyRequired: true},
        {label: 'Account IBAN', name: 'intermediaryAgentAccountIBAN', md: 3, maxLength: 50},
    ],
    [
        {label: 'Agent Name', name: 'intermediaryAgentName', md: 4, maxLength: 255},
        {label: 'Agent Department', name: 'intermediaryAgentDepartment', md: 4, maxLength: 70},
        {label: 'Agent Sub Department', name: 'intermediaryAgentSubDepartment', md: 4, maxLength: 70},
    ],
    [
        {label: 'Street Name', name: 'intermediaryAgentStreetName', md: 4, maxLength: 70},
        {label: 'Building Number', name: 'intermediaryAgentBuildingNumber', md: 2, maxLength: 16},
        {label: 'Building Name', name: 'intermediaryAgentBuildingName', md: 4, maxLength: 35},
        {label: 'Agent Floor', name: 'intermediaryAgentFloor', md: 2, maxLength: 70},
    ],
    [
        {label: 'Post Box', name: 'intermediaryAgentPostBox', md: 2, maxLength: 16},
        {label: 'Room', name: 'intermediaryAgentRoom', md: 2, maxLength: 70},
        {label: 'Post Code', name: 'intermediaryAgentPostCode', md: 2, maxLength: 16},
        {label: 'Town Name', name: 'intermediaryAgentTownName', md: 3, maxLength: 35, conditionallyRequired: true},
        {label: 'Town Location Name', name: 'intermediaryAgentTownLocationName', md: 3, maxLength: 35},
    ],
    [
        {label: 'District Name', name: 'intermediaryAgentDistrictName', md: 5, maxLength: 35},
        {label: 'Province', name: 'intermediaryAgentProvince', md: 4, maxLength: 35},
        {label: 'Country', name: 'intermediaryAgentCountry', md: 3, maxLength: 2, type: 'country', conditionallyRequired: true},
    ],
];

const INTERMEDIARY_AGENT_IDENTIFIER_ERROR = 'ERROR. Please check the Note above to fill in the required fields.';
const INTERMEDIARY_AGENT_CLEARING_SYSTEM_ID_FORMAT_ERROR = `Clearing System ID must start with ${USABA_CLEARING_SYSTEM_ID_PREFIX} followed by digits`;

const hasValue = (value) => value !== undefined && value !== null && value.toString().trim() !== '';
const isValidUSABAClearingSystemID = (clearingSystemID) => new RegExp(`^${USABA_CLEARING_SYSTEM_ID_PREFIX}\\d+$`).test(clearingSystemID.toString().trim());

export const getIntermediaryAgentClearingSystemIDWithPrefix = (clearingSystemID) => {
    if (!clearingSystemID) {
        return USABA_CLEARING_SYSTEM_ID_PREFIX;
    }

    const clearingSystemIDValue = clearingSystemID.toString();
    const clearingSystemIDValueUpperCase = clearingSystemIDValue.toUpperCase();

    if (USABA_CLEARING_SYSTEM_ID_PREFIX.startsWith(clearingSystemIDValueUpperCase) && clearingSystemIDValueUpperCase.length < USABA_CLEARING_SYSTEM_ID_PREFIX.length) {
        return USABA_CLEARING_SYSTEM_ID_PREFIX;
    }

    return getClearingSystemIdWithPrefix(clearingSystemIDValue);
}

export const validateStep5 = (addWireTemplateState = {}) => {
    let errors = {}
    const {
        intermediaryAgentBIC,
        intermediaryAgentClearingSystemCode,
        intermediaryAgentClearingSystemID,
        intermediaryAgentName,
        intermediaryAgentTownName,
        intermediaryAgentCountry,
    } = addWireTemplateState;

    const hasIntermediaryAgentName = hasValue(intermediaryAgentName);

    const hasBIC = hasValue(intermediaryAgentBIC);
    const hasClearingSystemCode = hasValue(intermediaryAgentClearingSystemCode);
    const hasClearingSystemID = hasValue(intermediaryAgentClearingSystemID);
    const hasTown = hasValue(intermediaryAgentTownName);
    const hasCountry = hasValue(intermediaryAgentCountry);

    if (hasIntermediaryAgentName && !hasBIC && (!hasClearingSystemCode || !hasClearingSystemID || !hasTown || !hasCountry)) {
        errors['intermediaryAgentBIC'] = INTERMEDIARY_AGENT_IDENTIFIER_ERROR;

        if (!hasClearingSystemCode) {
            errors['intermediaryAgentClearingSystemCode'] = INTERMEDIARY_AGENT_IDENTIFIER_ERROR;
        }

        if (!hasClearingSystemID) {
            errors['intermediaryAgentClearingSystemID'] = INTERMEDIARY_AGENT_IDENTIFIER_ERROR;
        }

        if (!hasTown) {
            errors['intermediaryAgentTownName'] = INTERMEDIARY_AGENT_IDENTIFIER_ERROR;
        }

        if (!hasCountry) {
            errors['intermediaryAgentCountry'] = INTERMEDIARY_AGENT_IDENTIFIER_ERROR;
        }
    }

    if (intermediaryAgentClearingSystemCode === USABA_CLEARING_SYSTEM_CODE && hasClearingSystemID && !isValidUSABAClearingSystemID(intermediaryAgentClearingSystemID)) {
        errors['intermediaryAgentClearingSystemID'] = INTERMEDIARY_AGENT_CLEARING_SYSTEM_ID_FORMAT_ERROR
    }

    return errors
}

export default function AddNewWireTemplateStep5() {

    const history = useHistory();
    const {addWireTemplateState, setAddWireTemplateState, clearAddNewWireTemplateState} = useContext(WireTemplatesContext);
    const {makeGetRequestNoParams} = useContext(SecurityContext);
    const [countries, setCountries] = useState([]);
    const [formErrors, setFormErrors] = useState({});

    const currentStepPage = '5'
    const backStepPage = '4'
    const nextStepPage = '6'

    const getCountries = useCallback(() => {
        const onSuccessResponse = (res) => {
            if (res.data) {
                setCountries(res.data);
            }
        }

        const onErrorResponse = (err) => {
            console.log("Error on get unique iso country code country name: ", err);
        }

        makeGetRequestNoParams(URL.COUNTRY_LIST_COUNTRY_NAME, onSuccessResponse, onErrorResponse)
    }, [makeGetRequestNoParams])

    useEffect(() => {
        getCountries()
    }, [getCountries])
    useEffect(() => {
        if (shouldNavigateToWireTemplateStep(currentStepPage, addWireTemplateState?.newPageStep)) {
            history.push(getWireTemplateStepUrl(addWireTemplateState.newPageStep))
        }
    }, [addWireTemplateState?.newPageStep, history])
    useEffect(() => {
        if (addWireTemplateState?.intermediaryAgentClearingSystemCode === USABA_CLEARING_SYSTEM_CODE) {
            const clearingSystemIDWithPrefix = getIntermediaryAgentClearingSystemIDWithPrefix(addWireTemplateState.intermediaryAgentClearingSystemID);

            if (addWireTemplateState.intermediaryAgentClearingSystemID !== clearingSystemIDWithPrefix) {
                setAddWireTemplateState({
                    ...addWireTemplateState,
                    intermediaryAgentClearingSystemID: clearingSystemIDWithPrefix,
                });
            }
        }
    }, [addWireTemplateState, setAddWireTemplateState])

    const handleChange = (e) => {
        const {name, value} = e.currentTarget;
        const newAddWireTemplateState = {...addWireTemplateState, [name]: value};

        if (name === 'intermediaryAgentClearingSystemID' && addWireTemplateState.intermediaryAgentClearingSystemCode === USABA_CLEARING_SYSTEM_CODE) {
            newAddWireTemplateState.intermediaryAgentClearingSystemID = getIntermediaryAgentClearingSystemIDWithPrefix(value);
        }

        if (name === 'intermediaryAgentClearingSystemCode' && value === USABA_CLEARING_SYSTEM_CODE) {
            newAddWireTemplateState.intermediaryAgentClearingSystemID = getIntermediaryAgentClearingSystemIDWithPrefix(addWireTemplateState.intermediaryAgentClearingSystemID);
        }

        setAddWireTemplateState(newAddWireTemplateState);
        if (formErrors[name]) {
            setFormErrors(previousErrors => ({...previousErrors, [name]: ''}))
        }
        if (name === 'intermediaryAgentName') {
            setFormErrors(validateStep5(newAddWireTemplateState))
        }
    }

    const updateIntermediaryAgentClearingSystemIDPrefix = (e) => {
        const {name, value} = e.currentTarget;

        if (name === 'intermediaryAgentClearingSystemID' && addWireTemplateState.intermediaryAgentClearingSystemCode === USABA_CLEARING_SYSTEM_CODE) {
            const clearingSystemIDWithPrefix = getIntermediaryAgentClearingSystemIDWithPrefix(value);

            if (value !== clearingSystemIDWithPrefix) {
                setAddWireTemplateState({
                    ...addWireTemplateState,
                    intermediaryAgentClearingSystemID: clearingSystemIDWithPrefix,
                });
            }
        }
    }

    const handleClear = (e) => {
        e.preventDefault();
        clearAddNewWireTemplateState();
        setFormErrors({});
    }

    const handleBack = (e) => {
        e.preventDefault();
        const newAddWireTemplateState = {...addWireTemplateState}
        newAddWireTemplateState['newPageStep']=backStepPage
        setAddWireTemplateState(newAddWireTemplateState)
        history.push(WIRE_TEMPLATE_STEP_URL + backStepPage);
    }

    const handleNext = (e) => {
        e.preventDefault();

        const errors = validateStep5(addWireTemplateState);
        if (Object.keys(errors).length !== 0) {
            setFormErrors(errors);
            return
        }

        const newAddWireTemplateState = {...addWireTemplateState}
        newAddWireTemplateState['newPageStep']=nextStepPage
        setAddWireTemplateState(newAddWireTemplateState)
        history.push(WIRE_TEMPLATE_STEP_URL + nextStepPage);
    }

    const renderRequiredMarker = (field) => {
        if (field.required) {
            return <span className='red-cell'>*</span>;
        }

        return field.conditionallyRequired && hasValue(addWireTemplateState.intermediaryAgentName) ? <span className='grey-cell'>*</span> : null;
    };

    const renderField = (field) => (
        <Col md={field.md} key={field.name}>
            <Form.Group controlId={`formWireTemplateStep6${field.name}`} className={field.type === 'country' || field.type === 'select' ? 'td-select' : ''}>
                <Form.Label column={false} className="filter-input-label">{field.label} {renderRequiredMarker(field)}</Form.Label>
                {field.type === 'country' ?
                    <Form.Control as="select" name={field.name} value={addWireTemplateState[field.name] || ''} onChange={handleChange}
                                  isInvalid={!!formErrors[field.name]}>
                        <option value={''}>Select</option>
                        {countries.map(country => (
                            <option key={country.countryCode} value={country.countryCode}>{country.country}</option>
                        ))}
                    </Form.Control> : field.type === 'select' ?
                    <Form.Control as="select" name={field.name} value={addWireTemplateState[field.name] || ''} onChange={handleChange}
                                  isInvalid={!!formErrors[field.name]}>
                        <option value={''}>Select</option>
                        {field.options.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </Form.Control> :
                    <Form.Control
                        name={field.name}
                        value={addWireTemplateState[field.name] || ''}
                        maxLength={field.maxLength}
                        onChange={handleChange}
                        onBlur={updateIntermediaryAgentClearingSystemIDPrefix}
                        onFocus={updateIntermediaryAgentClearingSystemIDPrefix}
                        isInvalid={!!formErrors[field.name]}
                    />
                }
                <Form.Control.Feedback type={'invalid'}>
                    <b><i className="bi bi-exclamation-circle"></i>{formErrors[field.name]}</b>
                </Form.Control.Feedback>
            </Form.Group>
        </Col>
    )

    const hasFormErrors = Object.keys(formErrors).some(Boolean);

    return (
        <WireTemplateWizardLayout currentStep={5} stepTitle="Intermediary Agent Details">
            <ShiftEnterPopupMessage />
            <Form>
                <div className={`awt-note ${hasFormErrors ? 'awt-note--error' : ''}`} role="note">
                    <b>Note:</b> If IntermediaryAgentName is populated please fill in IntermediaryAgentBIC OR IntermediaryAgentClearingSystemCode, IntermediaryAgentClearingSystemIDs,  IntermediaryAgentTown, and IntermediaryAgentCountry.
                    <p></p>
                    <b>Note:</b> Please populate Intermediary Agent Street Name and Intermediary Agent Province where possible.
                </div>
                {intermediaryAgentFields.map((fieldRow, index) => (
                    <Row key={index}>
                        {fieldRow.map(renderField)}
                    </Row>
                ))}

                <div className="awt-actions">
                    <button className="btn td-btn-secondary-clear" onClick={handleClear}>Clear</button>
                    <button className="btn td-btn-secondary-clear" onClick={handleBack}>Back</button>
                    <button className="btn td-btn-primary-light" onClick={handleNext}>Next</button>
                </div>
            </Form>
        </WireTemplateWizardLayout>
    )
}


